import html from './flag.html';
import {partial} from '../../../utils/partial';
import {Mouse} from '../../../utils/controllers/mouse';
import {emitter, Emitter, EVENTS} from '../../../utils/emitter';
import {randomInt} from '../../../utils/math/random';
import {clamp} from '../../../utils/math/clamp';
import {map} from '../../../utils/math/map';
import {backgroundContainer} from '../../../configs/global';
import {Window} from '../../../utils/controllers/window';
import {rootPath, ie11} from '../../../configs/global';
import {sleep} from '../../../utils/helpers';

import {TweenMax, Power4, Power1} from 'gsap';
import {Renderer, Camera, Transform, Texture, Program, Mesh, Vec2} from '../../../libraries/ogl/Core.js';
import {Plane, Sphere, Post} from '../../../libraries/ogl/Extras';

import FlagShader from './flag-shader';
import BackgroundShader from './flag-background-shader';
import VideoShader from './flag-video-shader';
import FXAAPass from './flag-fxaa-pass';
import {DEFAULTS, INTRO_TARGET, DEFAULTS_ABOUT, ABOUT_TARGET} from './flag-config';

export const tileSize = 1;
export const numY = 26;
export const numX = 48;

export class Flag extends Emitter {
  public element: HTMLElement;
  private renderer: Renderer;
  private gl: any;
  private camera: Camera;
  private post: Post;
  private passFXAA: any;

  private state = Object.assign({}, DEFAULTS) as any;

  private scene: Transform;
  private mesh: Mesh;
  private group: Transform;
  private flagProgram: Program;
  private backgroundProgram: Program;

  private video = false;
  private videoEl: HTMLVideoElement;
  private videoTexture: Texture;
  private videoMesh: Mesh;
  private videoProgram: Program;

  private targetX = 0;
  private targetY = 0;

  private stepAllow = false;

  private frame = 0;
  private running = false;

  private zoomMultiplier: number;

  constructor() {
    super();
    this.element = partial(html, 'section');

    this.initHTML();
    this.initRenderer();
    this.initPostVFX();
    this.initCamera();
    this.initFlag();
    this.initBackground();

    this.addHandlers();
    this.resize({width: Window.width, height: Window.height});

    this.stop();
  }

  private isSmall() {
    return Window.width < 800;
  }

  private initHTML() {
    const parent = document.querySelector(backgroundContainer);
    parent.appendChild(this.element);
  }

  private initRenderer() {
    // TODO: adjust based on device
    this.renderer = new Renderer({dpr: 2, alpha: false, antialias: false});
    this.gl = this.renderer.gl;
    this.gl.clearColor(0, 0, 0, 0);
    this.gl.canvas.classList.add('flag');
    this.element.appendChild(this.gl.canvas);
    this.scene = new Transform();
    this.group = new Transform();
    this.group.setParent(this.scene);
  }

  private initPostVFX() {
    this.post = new Post(this.gl);
    this.passFXAA = this.post.addPass({
      fragment: FXAAPass.fragment,
      vertex: FXAAPass.vertex,
      uniforms: {
        uTime: {value: 0},
        uResolution: {value: [Window.width, Window.height]}
      }
    });
  }

  private initCamera() {
    this.camera = new Camera(this.gl, {fov: 15, far: 3000});
    this.camera.position.z = 20;
    this.camera.lookAt([0, 0, 0]);
  }

  /**
   * This is only called for the intro.
   * There's no reason to create this video otherwise
   * because it's not shown on any other page.
   */
  private initVideo() {
    // Init empty texture while source loading
    const width = this.isSmall() ? 2 : 4;
    const height = 2;
    const videoGeometry = new Plane(this.gl, {width: tileSize * width, height: tileSize * height});
    const videoTexture = (this.videoTexture = new Texture(this.gl, {
      generateMipmaps: false,
      width: this.isSmall() ? 540 : 960,
      height: this.isSmall() ? 540 : 480
    }));

    // Fallback to static image
    if (ie11) {
      const img = new Image();
      img.onload = () => (videoTexture.image = img);
      img.src = this.isSmall()
        ? `${rootPath}assets/images/textures/intro_mobile.jpg`
        : `${rootPath}assets/images/textures/intro.jpg`;
    } else {
      // Create video with attributes that let it autoplay
      // Check update loop to see when video is attached to texture
      this.videoEl = document.createElement('video');
      this.videoEl.src = this.isSmall()
        ? `${rootPath}assets/video/intro_mobile.mp4`
        : `${rootPath}assets/video/intro.mp4`;
      this.videoEl.loop = true;
      this.videoEl.muted = true;
      this.videoEl.autoplay = true;
      this.videoEl.setAttribute('crossorigin', 'anonymous');
      this.videoEl.setAttribute('autoplay', 'true');
      this.videoEl.setAttribute('webkit-playsinline', 'true');
      this.videoEl.setAttribute('playsinline', 'true');
      this.videoEl.play();
    }

    const videoProgram = (this.videoProgram = new Program(this.gl, {
      vertex: VideoShader.vertex,
      fragment: VideoShader.fragment,
      uniforms: {
        tMap: {value: videoTexture},
        uAlpha: {value: this.state.videoAlpha},
        uColorMix: {value: this.state.videoColorMix}
      },
      transparent: true,
      depthWrite: false
    }));

    this.videoMesh = new Mesh(this.gl, {
      geometry: videoGeometry,
      program: videoProgram
    });

    this.videoMesh.renderOrder = 1;
    this.videoMesh.position.set(0, 0.0, 0.025);
    this.videoMesh.setParent(this.group);

    // tell the render loop to update uniforms
    this.video = true;
  }

  private initFlag() {
    const photos = new Texture(this.gl);
    const photosImg = new Image();
    photosImg.onload = () => (photos.image = photosImg);
    photosImg.src = `${rootPath}assets/images/textures/multi.jpg`;

    const overlay = new Texture(this.gl, {
      magFilter: this.gl.NEAREST,
      generateMipmaps: false
    });
    const overlayImg = new Image();
    overlayImg.onload = () => (overlay.image = overlayImg);
    overlayImg.src = `${rootPath}assets/images/textures/flag.jpg`;

    const program = (this.flagProgram = new Program(this.gl, {
      vertex: FlagShader.vertex,
      fragment: FlagShader.fragment,
      uniforms: {
        uTime: {value: 0},
        uOverlay: {value: 0},
        uProgressPoolA: {value: this.state.progressPoolA},
        uProgressPoolB: {value: this.state.progressPoolB},
        tMap: {value: photos},
        tOverlay: {value: overlay},
        uMaxAlpha: {value: this.state.maxFlagAlpha},
        uAlpha: {value: this.state.flagAlpha},
        uBrightnessMix: {value: this.state.flagBrightnessMix},
        uDissolve: {value: this.state.dissolve}
      },
      transparent: true
    }));

    const size = numX * numY;
    const offset = new Float32Array(size * 3);
    const random = new Float32Array(size * 3);
    const color = new Float32Array(size * 2);
    const xy = new Float32Array(size * 2);

    // TODO: Make two arrays.
    for (let y = 0; y < numY; y++) {
      for (let x = 0; x < numX; x++) {
        const i = numX * y + x;
        offset.set([x * tileSize, y * tileSize, 0], i * 3);
        xy.set([x, y], i * 2);

        // Blue
        if (y + 1 > numY - 14 && x < 20) {
          color.set([0, 1], i * 2);
        } else {
          if (y % 4 < 2) {
            // Red (two squares)
            color.set([1, randomInt(0, 1)], i * 2);
          } else {
            //  White
            color.set([0, 0], i * 2);
          }
        }

        random.set([Math.random(), Math.random(), Math.random()], i * 3);
      }
    }

    const geometry = new Plane(this.gl, {
      width: tileSize,
      height: tileSize,
      attributes: {
        offset: {instanced: true, size: 3, data: offset},
        random: {instanced: true, size: 3, data: random},
        color: {instanced: true, size: 2, data: color},
        xy: {instanced: true, size: 2, data: xy}
      }
    });

    this.mesh = new Mesh(this.gl, {geometry, program});
    this.mesh.position.x -= numX * 0.5 * tileSize - 0.5;
    this.mesh.position.y -= numY * 0.5 * tileSize - 0.5;
    this.mesh.position.z = 0;
    this.mesh.frustumCulled = false;
    this.mesh.setParent(this.group);
  }

  private initBackground() {
    const geometry = new Sphere(this.gl, {radius: 420});
    const program = (this.backgroundProgram = new Program(this.gl, {
      vertex: BackgroundShader.vertex,
      fragment: BackgroundShader.fragment,
      frontFace: this.gl.CW,
      uniforms: {
        uColorMix: {value: this.state.background}
      }
    }));

    // size and position
    const sphere = new Mesh(this.gl, {geometry, program});
    sphere.setParent(this.scene);
  }

  private stopVideo() {
    if (!this.videoEl) return;
    this.videoEl.src = '';
    this.videoEl = null;
  }

  public start() {
    if (this.running) return;
    this.running = true;
    this.frame = window.requestAnimationFrame(this.update);
    this.element.style.display = `block`;
  }

  public stop() {
    this.running = false;
    if (this.frame !== 0) window.cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.element.style.display = `none`;
  }

  // -- Events

  private update = (t?: number) => {
    this.frame = window.requestAnimationFrame(this.update);

    let time = t * 0.001;
    this.flagProgram.uniforms.uTime.value = time;
    this.flagProgram.uniforms.uProgressPoolA.value = this.state.progressPoolA;
    this.flagProgram.uniforms.uProgressPoolB.value = this.state.progressPoolB;
    this.flagProgram.uniforms.uMaxAlpha.value = this.state.maxFlagAlpha;
    this.flagProgram.uniforms.uAlpha.value = this.state.flagAlpha;
    this.flagProgram.uniforms.uBrightnessMix.value = this.state.flagBrightnessMix;
    this.flagProgram.uniforms.uDissolve.value = this.state.dissolve;
    this.flagProgram.uniforms.uOverlay.value = this.state.flagOverlay;

    this.backgroundProgram.uniforms.uColorMix.value = this.state.background;
    this.camera.position.z = 20 + this.state.zoom * this.zoomMultiplier;

    this.targetX = Mouse.centerX * 0.00005;
    this.targetY = Mouse.centerY * 0.00005;

    this.group.rotation.y += 0.05 * (this.targetX - this.group.rotation.y);
    this.group.rotation.x += 0.05 * (this.targetY - this.group.rotation.x);

    // If we have a video object in the scene update its properties
    // only created in if the intro animation is called
    if (this.video) {
      // Update uniforms (no matter if its a video or fallback image)
      this.videoProgram.uniforms.uAlpha.value = this.state.videoAlpha;
      this.videoProgram.uniforms.uColorMix.value = this.state.videoColorMix;
      this.videoMesh.position.z = this.state.videoZ;

      // if not ie 11 and we have an video element
      // attach video and/or update texture when video is ready
      if (this.videoEl && this.videoEl.readyState >= this.videoEl.HAVE_ENOUGH_DATA) {
        if (!this.videoTexture.image) this.videoTexture.image = this.videoEl;
        this.videoTexture.needsUpdate = true;
      }
    }

    if (this.post) {
      // this.passNoise.uniforms.uTime.value = time;
      this.passFXAA.uniforms.uTime.value = time;
      this.post.render({scene: this.scene, camera: this.camera});
    } else {
      this.renderer.render({scene: this.scene, camera: this.camera});
    }
  };

  private addHandlers() {
    emitter.on(EVENTS.resize, this.resize);
  }

  private resize = ({width, height}) => {
    this.zoomMultiplier = this.isSmall() ? 440 : 175;
    this.renderer.setSize(width, height);
    this.passFXAA.uniforms.uResolution.value = new Vec2(width, height);
    // this.passNoise.uniforms.uResolution.value = [width, height];
    this.camera.perspective({
      aspect: this.gl.canvas.width / this.gl.canvas.height
    });
    if (this.post) this.post.resize();
  };

  // -- Animateions

  /**
   * Use this to progess the intro.
   * Returns a promise that resolves when progress is hit.
   * @param {number} progess - number from 0-1;
   */
  public step(p: number) {
    if (!this.stepAllow) return;

    // TODO: Optimize this a bit and don't check on every call cache target values
    const small = this.isSmall();
    const scale = small ? 0.5 : 1;

    // Remap numbers
    let progressB = map(p, 0, small ? 0.15 : 0.35, INTRO_TARGET.progressPoolB, 1);
    progressB = clamp(0, 1, progressB);

    let brightness = map(p, small ? 0.1 : 0.23, small ? 0.25 : 0.45, INTRO_TARGET.flagBrightnessMix, 1);
    brightness = clamp(INTRO_TARGET.flagBrightnessMix, 1, brightness);

    let videoAlpha = map(p, 0, 0.06 * scale, INTRO_TARGET.videoAlpha, 0);
    videoAlpha = clamp(0, 1, videoAlpha);

    let dissolve = map(p, 0.84, 1, 0, 1);
    dissolve = clamp(0, 1, dissolve);

    let overlay = map(p, 0.2 * scale, 0.78 * scale, INTRO_TARGET.flagOverlay, 0.34);
    overlay = clamp(INTRO_TARGET.flagOverlay, 1, overlay);

    // Set state
    this.state.zoom = p;
    this.state.progressPoolB = progressB;
    this.state.flagBrightnessMix = brightness;
    this.state.videoAlpha = videoAlpha;
    this.state.background = progressB == 1 ? 1 : 0;
    this.state.dissolve = dissolve;
    this.state.flagOverlay = overlay;

    // lock scroll when at 1
    if (p >= 0.999) {
      this.stepAllow = false;
      this.stopVideo();
    }
  }

  /**
   * Animate in intro: First time the user visit the menu.
   * Autoplay to this before the user is in control and scroll to
   * finish the rest of the animation.
   */
  public animateInVideo() {
    this.state = Object.assign({}, DEFAULTS);
    this.initVideo();
    this.start();
    const delay = 0.85;
    return new Promise(resolve => {
      TweenMax.to(this.state, 2.5, {
        videoColorMix: INTRO_TARGET.videoColorMix,
        ease: Power4.easeOut,
        delay
      });
      TweenMax.to(this.state, 2.0, {
        videoZ: INTRO_TARGET.videoZ,
        ease: Power4.easeOut,
        delay
      });
      TweenMax.to(this.state, 5.85, {
        progressPoolA: INTRO_TARGET.progressPoolA,
        flagBrightnessMix: INTRO_TARGET.flagBrightnessMix,
        ease: Power1.easeOut,
        delay: delay + 0.15
      });

      sleep(delay + 4.5).then(() => {
        this.stepAllow = true;
        resolve();
      });
    });
  }

  /**
   * Animate flag to about state
   */
  public animateInDarkBackground(delay = 0) {
    this.state = Object.assign({}, DEFAULTS_ABOUT);
    this.start();
    return new Promise(resolve => {
      TweenMax.to(this.state, 0.4, {background: ABOUT_TARGET.background, ease: Power4.easeInOut});
      TweenMax.to(this.state, 2.0, {
        zoom: ABOUT_TARGET.zoom,
        progressPoolA: ABOUT_TARGET.progressPoolA,
        dissolve: ABOUT_TARGET.dissolve,
        flagBrightnessMix: ABOUT_TARGET.flagBrightnessMix,
        flagAlpha: ABOUT_TARGET.flagAlpha,
        delay: delay + 0.1,
        ease: Power4.easeOut,
        onComplete: resolve
      });
    });
  }

  /**
   * Animate flag out of about state
   */
  public animateOutDarkBackground(delay = 0) {
    this.start();
    return new Promise(resolve => {
      TweenMax.to(this.state, 2, {
        zoom: ABOUT_TARGET.zoom,
        progressPoolA: DEFAULTS_ABOUT.progressPoolA,
        dissolve: DEFAULTS_ABOUT.dissolve,
        flagAlpha: DEFAULTS_ABOUT.flagAlpha,
        delay,
        ease: Power4.easeOut
      });
      TweenMax.to(this.state, 0.85, {
        background: DEFAULTS_ABOUT.background,
        flagBrightnessMix: 1,
        delay: delay + 0.2,
        ease: Power4.easeIn,
        onComplete: () => {
          this.stop();
          resolve();
        }
      });
    });
  }
}
